import { NotFoundPage } from '@dec/shared';
import Redireccionar from 'app/modules/security/pages/redirect';
import Silent from 'app/modules/security/pages/silent';
import { SuccessTaxpayerRequest } from 'app/modules/Taxpayer/TaxpayerRequest/pages/success';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { GlobalStyle } from 'styles/global-styles';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { lazyLoad } from 'utils/loadable';

import { ViewTaxReturnPage } from '@/app/modules/tax-returns/pages/ViewTaxReturn/ViewTaxReturnPage';

import env from '../env';

import AuthCallback from './core/security/AuthCallback';
import AuthRedirect from './core/security/AuthRedirect';
import RouteAuth from './core/security/routeAuth';
import { PaymentModule } from './modules/payments/PaymentModule';
import { Login } from './modules/security/pages/login/login.page';
import { LoginWith2fa } from './modules/security/pages/loginWith2fa/loginWith2fa.page';
import { RecoverPassword } from './modules/security/pages/recoverPassword/recoverPassword.page';
import { RecoveryRequest } from './modules/security/pages/recoveryRequest/recoveryRequest.page';
import { TaxReturnModule } from './modules/tax-returns/TaxReturnModule';
import { TaxpayerRequest } from './modules/Taxpayer/TaxpayerRequest';
import { TaxpayerEditRequest } from './modules/Taxpayer/TaxpayerRequestEvaluation/components/editRequest';

// Lazy-loaded modules using lazyLoad helper (includes Suspense)
const AdministrationModule = lazyLoad(
  () => import('./modules/Administration/administration.module'),
  (m) => m.AdministrationModule
);

const InboxsModule = lazyLoad(
  () => import('./modules/Inbox/inbox.module'),
  (m) => m.InboxsModule
);

const ParametersModule = lazyLoad(
  () => import('./modules/Parameters/parameters.module'),
  (m) => m.ParametersModule
);

const ReportsModule = lazyLoad(
  () => import('./modules/Reports/reports.module'),
  (m) => m.ReportsModule
);

const SeguridadModule = lazyLoad(
  () => import('./modules/security/security.module'),
  (m) => m.SeguridadModule
);

const StatementModule = lazyLoad(
  () => import('./modules/Statements/statement.module'),
  (m) => m.StatementModule
);

const TaxesModule = lazyLoad(
  () => import('./modules/Taxes/taxes.module'),
  (m) => m.TaxesModule
);

const TaxpayerModule = lazyLoad(
  () => import('./modules/Taxpayer/taxpayer.module'),
  (m) => m.TaxpayerModule
);

const TaxStatementModule = lazyLoad(
  () => import('./modules/TaxStatement/taxStatement.module'),
  (m) => m.TaxStatementModule
);

// Lazy-loaded layouts
const DefaultLayout = lazyLoad(
  () => import('./layouts/default'),
  (m) => m.DefaultLayout
);

const LoginLayout = lazyLoad(
  () => import('./layouts/login'),
  (m) => m.LoginLayout
);

const PrintingLayout = lazyLoad(
  () => import('./layouts/printing'),
  (m) => m.PrintingLayout
);

const PublicLayout = lazyLoad(
  () => import('./layouts/public'),
  (m) => m.PublicLayout
);

const SpinnerLayout = lazyLoad(
  () => import('./layouts/spinner-layout'),
  (m) => m.SpinnerLayout
);

export function App() {
  const { t } = useTranslation();

  return (
    <BrowserRouter basename={env.basename}>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <title>{`${t('app.legend')} - ${t('app.name')}`}</title>
        <meta name="description" content={t('app.legend')} />

        <Routes>
          {/* procesa la redirección en la negociación de oauth */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          <Route
            path="/login"
            element={
              <LoginLayout>
                <Login />
              </LoginLayout>
            }
          />
          <Route
            path="/loginWith2fa"
            element={
              <LoginLayout>
                <LoginWith2fa />
              </LoginLayout>
            }
          />
          {/* Página para la definicion de una nueva contraseña de usuario
          luego de reiniciar conrtaseña mediante opción en login */}
          <Route
            path="/authentication/restorepassword"
            element={
              <LoginLayout>
                <RecoverPassword />
              </LoginLayout>
            }
          />
          <Route
            path="/authentication/changepassword"
            element={
              <LoginLayout>
                <RecoverPassword changePassword={true} />
              </LoginLayout>
            }
          />
          <Route
            path="/public/taxpayer-request/success"
            element={
              <div className="w-100 vh-100">
                <div className="container h-100">
                  <div className="row align-items-center h-100">
                    <SuccessTaxpayerRequest />
                  </div>
                </div>
              </div>
            }
          />
          <Route
            path="/public/taxpayer-request"
            element={
              <PublicLayout>
                <TaxpayerRequest />
              </PublicLayout>
            }
          />
          <Route
            path="/public/taxpayer-request/:id"
            element={
              <SpinnerLayout>
                <TaxpayerEditRequest />
              </SpinnerLayout>
            }
          />
          <Route
            path="/silent"
            element={
              <LoginLayout>
                <Silent />
              </LoginLayout>
            }
          />
          <Route
            path="/redirect"
            element={
              <LoginLayout>
                <Redireccionar />
              </LoginLayout>
            }
          />

          {/* Recuperar contraseña dede login si ha sido olvidada (envia por correo vinculo) */}
          <Route
            path="/recovery-request"
            element={
              <SpinnerLayout>
                <LoginLayout>
                  <RecoveryRequest redirectKey="appUrl" />
                </LoginLayout>
              </SpinnerLayout>
            }
          />
          <Route
            path="/taxpayer/tax-returns/preview/:taxReturnId"
            element={
              <AuthRedirect>
                <PrintingLayout>
                  <ViewTaxReturnPage />
                </PrintingLayout>
              </AuthRedirect>
            }
          />

          <Route
            path="/payments/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <PaymentModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />

          <Route
            path="/tax-returns/*"
            element={
              <AuthRedirect>
                <DefaultLayout>
                  <TaxReturnModule />
                </DefaultLayout>
              </AuthRedirect>
            }
          />

          <Route
            path="/*"
            element={
              <AuthRedirect>
                <RouteAuth>
                  <DefaultLayout>
                    <Routes>
                      <Route path="administration/*" element={<AdministrationModule />} />
                      <Route path="parameters/*" element={<ParametersModule />} />
                      <Route path="security/*" element={<SeguridadModule />} />
                      <Route path="taxpayer/*" element={<TaxpayerModule />} />
                      <Route path="tax-statement/*" element={<TaxStatementModule />} />
                      <Route path="inbox/*" element={<InboxsModule />} />
                      <Route path="statements/*" element={<StatementModule />} />
                      <Route path="reports/*" element={<ReportsModule />} />
                      <Route path="taxes/*" element={<TaxesModule />} />
                      <Route index element={<ParametersModule />} />
                    </Routes>
                  </DefaultLayout>
                </RouteAuth>
              </AuthRedirect>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <GlobalStyle />
      </QueryParamProvider>
    </BrowserRouter>
  );
}
